import React from 'react';
import { DefaultValues } from '../elements/DashboardElement.tsx';
import { getDefaultStartDate, getDefaultEndDate } from '../utils/dateLimits.tsx';

export interface FilterContextEl {
    idDashboard?: string, 
    name: string;
    id: string;
    filtered?: string[];
    impacted?: string[];
    optionList: {name: string, id: string, root:any}[],
    type: string;
    selectedValue?: any|undefined,
    filteredselectedValue?: any|undefined,
    isAllSelected?: boolean,
}

interface FilterContextType {
    addFilters: (idDashboard: string, filters: FilterContextEl[], multiple: boolean[]) => FilterContextEl[];
    isAllSelected: (filterName: string) => boolean|undefined;
    getFilterValueList: (filterName: string) => any|undefined;
    setFilterValueList: (filterName: string, newFilter: any, clickedOption: any) => void;
    getFilteredOptionList: (filterElement: any, selectedOptions: any) => {name: string}[]; 
}

const FilterContext = React.createContext<FilterContextType | undefined>(undefined);

export const FilterProvider: React.FC<{ children: React.ReactNode }> = ({children}) => {
    const [filters, setFilters] = React.useState<FilterContextEl[]>([]);

    React.useEffect(() => {
      const dateFilterList = [{ name:'startDate', id:'startDate', optionList:[], type: 'datepicker', selectedValue: getDefaultStartDate() },
        { name:'endDate', id:'endDate', optionList:[], type: 'datepicker', selectedValue: getDefaultEndDate() },
      ]
      setFilters(dateFilterList)
    },[])

    const addFilters = (idDashboard: string, filtersList: FilterContextEl[], multiple: boolean[]) => {
      const selectedList: FilterContextEl[] = [...filters]
      filtersList.forEach((filter, index) => {
          filter.idDashboard = idDashboard;
          if(filter.optionList.length > 0 && multiple[index]) filter.optionList = [{name: 'All', id: 'All', root:[]},...filter.optionList]
          const currentFilter = selectedList.find(obj => obj.id === filter.id);
          if (currentFilter) {
            currentFilter.selectedValue = (currentFilter.type === 'selector')
              ?filter.optionList.filter(el => el.name === DefaultValues[filter.name])[0]
              :getFilterValueList(filter.id); 
            filter.selectedValue = currentFilter.selectedValue;
          }
          else {
            if (filter.type === 'selector') {filter.selectedValue = filter.optionList.filter(el => el.name === DefaultValues[filter.name])[0]}
            selectedList.push(filter)
          }
      })
      setFilters([...selectedList])
      return filtersList.filter((filter) => filter.type !== 'datepicker');
    };

    const getFilterValueList = (filterName: String) => {  
        try{
          if (filterName !== ''){
            const currentFilter = filters.find((el) => el.id === filterName)
            if (currentFilter)
              return currentFilter.selectedValue
            }
          }
        catch(error){}
        return [];
    };

    const isAllSelected = (filterName) => {
      let currentFilter: FilterContextEl|undefined = filters.find(obj => obj.id === filterName);
      // Controlla se il filtro esiste già
      if (currentFilter)
        return currentFilter.isAllSelected
      return false
    }

    const setFilterValueList = (filterName: any, newFilter: any, clickedOption: any) => {
        setFilters((prevFilters) => {
          if(typeof filterName !== 'string') filterName = filterName.id
          let currentFilter: FilterContextEl|undefined = prevFilters.find(obj => obj.id === filterName);
          // Controlla se il filtro esiste già
          if (currentFilter) {
            if(currentFilter.type === "multiselector") {
              const optionList = getFilteredOptionList(currentFilter, (currentFilter.filtered)? getFilterValueList(currentFilter.filtered[currentFilter.filtered.length - 1 ]) || []:[])
              if(clickedOption === 'All') {
                if (!currentFilter.isAllSelected) {
                  currentFilter.isAllSelected = true;
                  currentFilter.selectedValue = currentFilter.filteredselectedValue.filter(obj => obj.id !== 'All') ||  optionList.filter(obj => obj.id !== 'All');
                } else {
                  currentFilter.isAllSelected = false;
                  currentFilter.selectedValue = [];
                }
              } else {
                if(currentFilter.type === "multiselector") {
                  if(clickedOption === null || clickedOption === undefined || (currentFilter.selectedValue && currentFilter.selectedValue.find(obj => obj.id === clickedOption)) ){ // click sulla 'x'
                    currentFilter.isAllSelected = false;
                  }
                  if (!filterName.includes('data') && clickedOption !== 'All' && currentFilter.isAllSelected) {
                    currentFilter.selectedValue = currentFilter.filteredselectedValue.filter(obj => obj.id !== 'All') || newFilter.filter((el) => el.id !== 'All');
                  }
                  else currentFilter.selectedValue = newFilter;
                }
              }
              if(currentFilter.impacted && 'idDashboard' in currentFilter) {
                currentFilter.impacted.forEach((filter) => {
                const impactedFilters = prevFilters.filter(obj => obj.id === currentFilter.idDashboard+'_'+filter || obj.id.endsWith('_'+filter));

                impactedFilters.forEach((impactedFilter) => {
                  impactedFilter.selectedValue = (impactedFilter.type === "selector")
                    ?impactedFilter.optionList.filter(el => el.name === DefaultValues[impactedFilter.name])[0]
                    :[]
                  impactedFilter.isAllSelected= false
                })
              })}
            }
            else{
              currentFilter.selectedValue = (Array.isArray(newFilter) && newFilter.length === 0)?currentFilter.optionList.filter(el => el.name === DefaultValues[currentFilter.name])[0]:newFilter
            }
          }
          return [...prevFilters];
        });
    };

    const getFilteredOptionList = (filterElement: any, selectedOptions: any) => {
      if(selectedOptions.length > 0){
        selectedOptions = selectedOptions.map(el => el.id)
        let newList = filterElement.optionList.filter(el => el.root && (el.root.length === 0 || el.root.some(rootItem => selectedOptions.includes(rootItem))) || el.id === 'All')
        if (newList.length === 1 && newList[0].id==='All') newList = []
        filterElement.filteredselectedValue = newList
        return newList
      }
      return filterElement.optionList || []
    };
 
    return (
    <FilterContext.Provider
      value={{
        addFilters,
        isAllSelected,
        getFilterValueList,
        setFilterValueList,
        getFilteredOptionList
      }}>
      {children}
    </FilterContext.Provider>
  );
};

export const useFilter = (): FilterContextType => {
  const context = React.useContext(FilterContext);
  if (!context) {
    throw new Error('useFilter must be used within an FilterProvider');
  }
  return context;
};